import currencies, { getCurrencyMode } from '@/util/currencies'
import storage from '@/util/storage'
import requestService from '@/services/request'
import { createSlice, createSelector } from '@reduxjs/toolkit'
import { urlSearchParams } from '@/util/urlSearchParams'
import constants from '@/constants'

const currentStoredCurrency =
  typeof window !== 'undefined' ? localStorage.getItem('userCurrency')?.replace(/['"]/g, '') : ''
const resolvedCurrency = currentStoredCurrency || currencies.OOM.id
const initialState = {
  marketType: getCurrencyMode(resolvedCurrency),
  activeCurrency: resolvedCurrency,
  tempCurrency: null,
  language: null,
  /** Bumped when locale dictionaries finish loading so UI re-renders with i18n(). */
  i18nVersion: 0,
  info: null,
  layout: {
    appLoading: true,
    showNavigationBar: true,
    navbarLogoOnly: false,
    showTabBar: true,
    transparentNavbar: false,
    hideNavbarInfos: false,
    removeNavbar: false,
    displayFooter: true,
    goToWebVersionDisplayed: false,
    currencySelectorOpen: false,
    notificationWidgetOpen: false,
    theme: 'light',
    adminMode: null,
  },
  exchangeRates: { data: {}, status: constants.IDLE },
  withdrawFees: { data: {}, status: constants.IDLE, error: null },
  abTests: { data: {}, status: constants.IDLE, error: null },
  outcomeSelected: null,
  commentLength: 0,
  selectedLimitPrice: null,
  limitOrderFillPreview: null,
}

// Reducers / Slices

const app = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setActiveCurrency: (state, action) => {
      const currency = action.payload
      if (typeof currency === 'object' && currency.id) {
        state.activeCurrency = currency.id
      }

      if (typeof currency === 'string') {
        state.activeCurrency = currency
      }

      setMarketType(state, { payload: getCurrencyMode(state.activeCurrency) })
    },

    setInfo: (state, action) => {
      state.info = action.payload
    },
    getCommentLength: (state, action) => {
      state.info = action.payload
    },

    parseActiveCurrency: state => {
      var currencyId = state.activeCurrency
      if (currencyId === constants.REAL_MONEY_PLACEHOLDER) {
        currencyId = constants.CANONICAL_CURRENCY
      }
      state.activeCurrency = currencyId
    },

    setMarketType: (state, action) => {
      state.marketType = action.payload
    },

    setTempCurrency: (state, action) => {
      state.tempCurrency = action.payload
    },

    setNavigationBarVisibility: (state, action) => {
      state.layout.showNavigationBar = action.payload
    },
    setAppLoading: (state, action) => {
      state.layout.appLoading = action.payload
    },
    setNavigationBarLogoOnly: (state, action) => {
      state.layout.navbarLogoOnly = action.payload
    },

    setTransparentNavbar: (state, action) => {
      state.layout.transparentNavbar = action.payload
    },

    setTabBarVisibility: (state, action) => {
      state.layout.showTabBar = action.payload
    },

    setRemoveNavbar: (state, action) => {
      state.layout.removeNavbar = action.payload
    },

    setShowNavigationBar: (state, action) => {
      state.layout.showNavigationBar = action.payload
    },

    setNavbarInfosVisibility: (state, action) => {
      state.layout.hideNavbarInfos = action.payload
    },

    setGoToWebVersionDisplayed: (state, action) => {
      state.layout.goToWebVersionDisplayed = action.payload
    },

    setDisplayFooter: (state, action) => {
      state.layout.displayFooter = action.payload
    },

    setCurrencySelectorOpen: (state, action) => {
      state.layout.currencySelectorOpen = action.payload
    },
    setNotificationWidgetOpen: (state, action) => {
      state.layout.notificationWidgetOpen = action.payload
    },
    setAppTheme: (state, action) => {
      state.layout.theme = action.payload
    },
    setActiveLanguage: (state, action) => {
      state.language = action.payload
      state.i18nVersion = (state.i18nVersion || 0) + 1
    },
    bumpI18nVersion: state => {
      state.i18nVersion = (state.i18nVersion || 0) + 1
    },

    getExchangeRatesStart: state => {
      state.exchangeRates.status = constants.LOADING
    },

    getExchangeRatesSuccess: (state, action) => {
      const { exchangeRates } = action.payload
      state.exchangeRates.status = constants.COMPLETE
      state.exchangeRates.data = exchangeRates
    },

    getExchangeRatesError: (state, action) => {
      const { error } = action.payload
      state.exchangeRates.status = constants.ERROR
      state.exchangeRates.error = error
    },

    getWithdrawFeesStart: state => {
      state.withdrawFees.status = constants.LOADING
    },

    getWithdrawFeesSuccess: (state, action) => {
      const { fees } = action.payload
      state.withdrawFees.data = fees
      state.withdrawFees.status = constants.COMPLETE
    },

    getWithdrawFeesError: (state, action) => {
      const { error } = action.payload
      state.withdrawFees.error = error
      state.withdrawFees.status = constants.ERROR
    },
    getAbTestsStart: state => {
      state.abTests.status = constants.LOADING
    },
    getAbTestsSuccess: (state, action) => {
      const { abTests } = action.payload
      state.abTests.data = abTests
      state.abTests.status = constants.COMPLETE
    },
    getAbTestsError: (state, action) => {
      const { error } = action.payload
      state.abTests.error = error
      state.abTests.status = constants.ERROR
    },
    getAdminMode: (state, action) => {
      state.layout.adminMode = action.payload
    },

    setOutcomeSelected: (state, action) => {
      state.outcomeSelected = action.payload
    },

    setSelectedLimitPrice: (state, action) => {
      state.selectedLimitPrice = action.payload
    },
    clearSelectedLimitPrice: state => {
      state.selectedLimitPrice = null
    },
    setLimitOrderFillPreview: (state, action) => {
      state.limitOrderFillPreview = action.payload
    },
    clearLimitOrderFillPreview: state => {
      state.limitOrderFillPreview = null
    },
  },
})

export const {
  setActiveCurrency,
  setMarketType,
  setNavigationBarVisibility,
  setAppLoading,
  setNavigationBarLogoOnly,
  setTransparentNavbar,
  setTabBarVisibility,
  setNavbarInfosVisibility,
  setActiveLanguage,
  bumpI18nVersion,
  setTempCurrency,
  getAdminMode,
  setRemoveNavbar, 
  setCurrencySelectorOpen,
  setNotificationWidgetOpen,
  setAppTheme, 
  getExchangeRatesStart,
  setShowNavigationBar,
  getExchangeRatesSuccess,
  getExchangeRatesError,
  parseActiveCurrency,
  setDisplayFooter,
  getWithdrawFeesSuccess,
  setInfo,
  getCommentLength,
  getWithdrawFeesError,
  getWithdrawFeesStart,
  setGoToWebVersionDisplayed,
  getAbTestsStart,
  getAbTestsSuccess,
  getAbTestsError,
  setOutcomeSelected,
  setSelectedLimitPrice,
  clearSelectedLimitPrice,
  setLimitOrderFillPreview,
  clearLimitOrderFillPreview,
} = app.actions

export default app.reducer

// Selects

export const selectActiveMarketType = state => getCurrencyMode(state.app.activeCurrency)
export const selectActiveCurrency = state => state.app.activeCurrency
export const selectPageLayout = state => state.app.layout
export const selectInfo = state => state.app.info
export const selectCommentLength = state => state.app.commentLength
export const selectAbTestsStatus = state => state.app.abTests.status
export const selectOutcomeSelected = state => state.app.outcomeSelected
export const selectAllExchangeRate = () => state => state.app.exchangeRates.data

// Memoized selector factory for exchange rates
const selectExchangeRateCache = new Map()

export const selectExchangeRate = (currencyId, canonicalCurrency) => {
  const cacheKey = `${currencyId || 'null'}_${canonicalCurrency || 'null'}`

  if (!selectExchangeRateCache.has(cacheKey)) {
    const memoizedSelector = createSelector([state => state.app.exchangeRates.data], exchangeRatesData => {
      if (currencyId === currencies.OOM.id) {
        return 1
      }

      if (!exchangeRatesData) {
        return null
      }

      return (
        exchangeRatesData &&
        exchangeRatesData[canonicalCurrency] &&
        exchangeRatesData[canonicalCurrency].rates &&
        exchangeRatesData[canonicalCurrency].rates[currencyId]
      )
    })
    selectExchangeRateCache.set(cacheKey, memoizedSelector)
  }

  return selectExchangeRateCache.get(cacheKey)
}

export const selectExchangeRateStatus = () => state => state.app.exchangeRates.status

// Memoized selector factory for exchange rate update codes
const selectExchangeRateUpdateCodeCache = new Map()

export const selectExchangeRateUpdateCode = (currencyId = 'USDC') => {
  const cacheKey = currencyId || 'USDC'

  if (!selectExchangeRateUpdateCodeCache.has(cacheKey)) {
    const memoizedSelector = createSelector([state => state.app.exchangeRates.data], exchangeRatesData => {
      if (!exchangeRatesData) {
        return null
      }
      return exchangeRatesData && exchangeRatesData[currencyId] && exchangeRatesData[currencyId].update_code
    })
    selectExchangeRateUpdateCodeCache.set(cacheKey, memoizedSelector)
  }

  return selectExchangeRateUpdateCodeCache.get(cacheKey)
}

export const selectTempCurrency = () => state => state.app.tempCurrency || state.app.activeCurrency

export const selectActiveLanguage = locale => state => {
  const normalizeLanguage = language => {
    let l = language.toLowerCase().replace(new RegExp('_', 'g'), '-')
    if (l.indexOf('en') > -1) l = 'en'
    return l
  }

  if (locale) {
    return normalizeLanguage(locale)
  }

  if (state.app && state.app.language) {
    return normalizeLanguage(state.app.language)
  }

  if (typeof window !== 'undefined' && window.location) {
    const urlParams = new urlSearchParams(window.location.search)
    if (urlParams.get('lg')) {
      return normalizeLanguage(urlParams.get('lg'))
    }
  }

  if (global.navigator?.languages?.length > 0 && global.navigator.languages[0]) {
    return normalizeLanguage(global.navigator.languages[0].toLowerCase())
  }

  if (global.navigator?.language) {
    return normalizeLanguage(global.navigator.language.toLowerCase())
  }

  return 'en'
}

export const selectWithdrawFees = () => state => state.app.withdrawFees.data
export const selectWithdrawFeesStatus = () => state => state.app.withdrawFees.status
export const selectWithdrawFeesError = () => state => state.app.withdrawFees.error
export const selectSelectedLimitPrice = state => state.app.selectedLimitPrice
export const selectLimitOrderFillPreview = state => state.app.limitOrderFillPreview

// Thunks

export const storeActiveCurrency = currency => async dispatch => {
  dispatch(setActiveCurrency(currency))
  storage('userCurrency').set(currency)
}
 
/**
 * Exchange rates contains all information to convert between currencies.
 * Ex: 1 BTC = 16.29743985 ETH
 */
export const fetchExchangeRates = () => async dispatch => {
  try {
    dispatch(getExchangeRatesStart())
    const response = await requestService.get(`/exchange_rates/latest/`)
    const exchangeRates = await response.json()
    dispatch(getExchangeRatesSuccess({ exchangeRates }))
  } catch (err) {
    dispatch(getExchangeRatesError(err))
  }
}

export const fetchWithdrawFees = () => async dispatch => {
  try {
    dispatch(getWithdrawFeesStart())
    const response = await requestService.get(`/finance/withdrawal_fees/`)
    const fees = await response.json()
    dispatch(getWithdrawFeesSuccess({ fees }))
  } catch (e) {
    dispatch(getWithdrawFeesError(e))
  }
}

export const fetchInfo = () => async dispatch => {
  try {
    const response = await requestService.get(`/info/`)
    const info = await response.json()
    dispatch(setInfo({ info }))
  } catch (e) {
    console.error('Info not found', e)
  }
}
export const fetchABTests = () => async dispatch => {
  try {
    dispatch(getAbTestsStart())
    const response = await requestService.get(`/ab_tests/`)
    const { results } = await response.json()
    const abTests = Array.isArray(results) ? results : []
    dispatch(getAbTestsSuccess({ abTests }))
    return abTests
  } catch (e) {
    // AB tests are optional: if this fails we still want the rest
    // of the app (and market fetching) to proceed.
    console.error('Error fetching AB tests', e)
    dispatch(getAbTestsSuccess({ abTests: [] }))
    return []
  }
}
